'use client';

import type { IHelpExampleSlots, THelpExampleLang } from '@/interfaces/helpExamples';
import { DEFAULT_EXAMPLE_EDITION, exampleFragment } from '@/lib/help-examples';
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'help-example-edition';

/**
 * "Show examples for: ▾" on help articles that carry example slots. The page is
 * rendered with the default edition; each slot sits between
 * `<!--example:slot-->…<!--/example:slot-->` comments, and picking a business
 * type swaps just those ranges — the explanation around them never changes.
 */
export default function HelpExamplePicker({
  examples,
  editions,
  lang,
  articleSelector,
}: {
  examples: IHelpExampleSlots;
  editions: { slug: string; label: string }[];
  lang: THelpExampleLang;
  /** CSS selector of the element whose HTML holds the slot comments. */
  articleSelector: string;
}) {
  const [edition, setEdition] = useState(DEFAULT_EXAMPLE_EDITION);

  // Remember the visitor's business type across articles (per-browser convenience only).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && editions.some(e => e.slug === saved)) setEdition(saved);
    } catch {
      /* storage unavailable */
    }
  }, [editions]);

  useEffect(() => {
    const root = document.querySelector(articleSelector);
    if (!root) return;
    for (const slot of Object.keys(examples)) {
      const start = findComment(root, `example:${slot}`);
      const end = findComment(root, `/example:${slot}`);
      if (!start || !end || start.parentNode !== end.parentNode) continue;
      const range = document.createRange();
      range.setStartAfter(start);
      range.setEndBefore(end);
      range.deleteContents();
      range.insertNode(
        range.createContextualFragment(exampleFragment(examples, slot, edition, lang))
      );
    }
  }, [edition, examples, lang, articleSelector]);

  const onChange = (value: string) => {
    setEdition(value);
    try {
      if (value === DEFAULT_EXAMPLE_EDITION) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* storage unavailable */
    }
  };

  return (
    <label
      className="flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm not-prose"
      style={{
        borderColor: 'var(--color-input-border)',
        backgroundColor: 'var(--color-input-background)',
        color: 'var(--color-foreground)',
      }}
    >
      <span className="font-medium">
        {lang === 'en' ? 'Show examples for:' : '顯示適用於此業務的範例：'}
      </span>
      <select
        value={edition}
        onChange={e => onChange(e.target.value)}
        className="rounded-lg border px-3 py-2 outline-none focus:ring-2"
        style={{
          borderColor: 'var(--color-input-border)',
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-input-text)',
        }}
      >
        <option value={DEFAULT_EXAMPLE_EDITION}>
          {lang === 'en' ? 'General examples' : '一般範例'}
        </option>
        {editions.map(e => (
          <option key={e.slug} value={e.slug}>
            {e.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function findComment(root: Element, text: string): Comment | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue?.trim() === text) return node as Comment;
  }
  return null;
}
